import React, { useRef, useState, useEffect, useCallback } from "react";
import "./EpisodePlayer.css";

// Format seconds to mm:ss
function formatTime(seconds) {
  if (isNaN(seconds) || seconds < 0) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

// Lantern icon (same as list, inline for self-containment)
function LanternIcon({ size = 24, glow = false }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      style={glow ? { filter: "drop-shadow(0 0 12px rgba(200,137,58,0.7))" } : {}}
    >
      <rect x="9" y="1" width="6" height="2" rx="1" fill="currentColor" opacity="0.6" />
      <rect x="11" y="3" width="2" height="2" fill="currentColor" opacity="0.4" />
      <rect x="7" y="5" width="10" height="14" rx="2" stroke="currentColor" strokeWidth="1.2" fill="none" />
      <line x1="7" y1="8" x2="17" y2="8" stroke="currentColor" strokeWidth="0.8" opacity="0.5" />
      <line x1="7" y1="16" x2="17" y2="16" stroke="currentColor" strokeWidth="0.8" opacity="0.5" />
      <ellipse cx="12" cy="12" rx="2.5" ry="3" fill="currentColor" opacity="0.25" />
      <ellipse cx="12" cy="11.5" rx="1.2" ry="1.5" fill="currentColor" opacity="0.5" />
      <rect x="9" y="19" width="6" height="2" rx="1" fill="currentColor" opacity="0.4" />
      <rect x="10" y="21" width="4" height="1.5" rx="0.75" fill="currentColor" opacity="0.3" />
    </svg>
  );
}

// Play / Pause icons
function PlayIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="currentColor" aria-hidden="true">
      <polygon points="6,4 24,14 6,24" />
    </svg>
  );
}

function PauseIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="currentColor" aria-hidden="true">
      <rect x="5" y="4" width="7" height="20" rx="1.5" />
      <rect x="16" y="4" width="7" height="20" rx="1.5" />
    </svg>
  );
}

// Skip back 30s icon
function SkipBackIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      {/* Circular arrow */}
      <path
        d="M12 5V2L7 6l5 4V7a6 6 0 1 1-5.93 5H4.06A8 8 0 1 0 12 5z"
        fill="currentColor"
      />
      {/* 30 label */}
      <text
        x="12"
        y="16"
        fontSize="5"
        fontFamily="sans-serif"
        fontWeight="600"
        fill="currentColor"
        textAnchor="middle"
      >
        30
      </text>
    </svg>
  );
}

export default function EpisodePlayer({ episode, onBack }) {
  const audioRef = useRef(null);
  const progressRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.8);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Sync time display
  const handleTimeUpdate = useCallback(() => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  }, []);

  const handleLoadedMetadata = useCallback(() => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
      setIsLoading(false);
    }
  }, []);

  const handleEnded = useCallback(() => {
    setIsPlaying(false);
    setCurrentTime(0);
  }, []);

  const handleError = useCallback(() => {
    setError("Audio unavailable. This episode will be ready soon.");
    setIsLoading(false);
    setIsPlaying(false);
  }, []);

  // Attach audio event listeners
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.volume = volume;
    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("error", handleError);
    return () => {
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("error", handleError);
    };
  }, [handleTimeUpdate, handleLoadedMetadata, handleEnded, handleError, volume]);

  // Play / Pause
  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      setIsLoading(true);
      audio.play()
        .then(() => {
          setIsPlaying(true);
          setIsLoading(false);
          setError(null);
        })
        .catch(() => {
          setError("Audio unavailable. This episode will be ready soon.");
          setIsLoading(false);
        });
    }
  };

  // Seek via progress bar click
  const handleProgressClick = (e) => {
    if (!audioRef.current || !duration) return;
    const rect = progressRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const ratio = Math.max(0, Math.min(1, x / rect.width));
    audioRef.current.currentTime = ratio * duration;
    setCurrentTime(ratio * duration);
  };

  // Skip back 30s
  const skipBack = () => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = Math.max(0, audioRef.current.currentTime - 30);
  };

  // Volume
  const handleVolumeChange = (e) => {
    const v = parseFloat(e.target.value);
    setVolume(v);
    if (audioRef.current) audioRef.current.volume = v;
  };

  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="player-page">
      {/* Hidden audio element — streaming, not downloadable */}
      <audio
        ref={audioRef}
        src={episode.audioUrl}
        preload="metadata"
        controlsList="nodownload"
      />

      {/* Back nav */}
      <nav className="player-nav">
        <button className="back-btn" onClick={onBack}>
          ← All episodes
        </button>
      </nav>

      {/* Main player */}
      <main className="player-main">
        {/* Lantern artwork area */}
        <div className="player-artwork">
          <div className="lantern-glow" />
          <div className="lantern-icon-wrap">
            <LanternIcon size={56} glow={isPlaying} />
          </div>
        </div>

        {/* Episode info */}
        <div className="player-info">
          <span className="player-theme">{episode.theme}</span>
          <h1 className="player-title">{episode.title}</h1>
          <p className="player-description">{episode.description}</p>
        </div>

        {/* Error state */}
        {error && (
          <div className="player-error">
            <span>🌙</span> {error}
          </div>
        )}

        {/* Controls */}
        <div className="player-controls">
          {/* Progress bar */}
          <div className="progress-section">
            <span className="time-label">{formatTime(currentTime)}</span>
            <div
              className="progress-track"
              ref={progressRef}
              onClick={handleProgressClick}
              role="slider"
              aria-label="Playback progress"
              aria-valuenow={Math.round(progressPercent)}
              aria-valuemin={0}
              aria-valuemax={100}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "ArrowRight") {
                  if (audioRef.current) audioRef.current.currentTime = Math.min(duration, currentTime + 10);
                }
                if (e.key === "ArrowLeft") {
                  if (audioRef.current) audioRef.current.currentTime = Math.max(0, currentTime - 10);
                }
              }}
            >
              <div
                className="progress-fill"
                style={{ width: `${progressPercent}%` }}
              />
              <div
                className="progress-thumb"
                style={{ left: `${progressPercent}%` }}
              />
            </div>
            <span className="time-label">{formatTime(duration)}</span>
          </div>

          {/* Buttons row */}
          <div className="buttons-row">
            <button
              className="control-btn secondary"
              onClick={skipBack}
              aria-label="Skip back 30 seconds"
              title="Back 30s"
            >
              <SkipBackIcon />
              <span className="btn-label">30s</span>
            </button>

            <button
              className={`control-btn play-btn ${isPlaying ? "playing" : ""} ${isLoading ? "loading" : ""}`}
              onClick={togglePlay}
              aria-label={isPlaying ? "Pause" : "Play"}
            >
              {isLoading ? (
                <div className="spinner" />
              ) : isPlaying ? (
                <PauseIcon />
              ) : (
                <PlayIcon />
              )}
            </button>

            {/* Volume */}
            <div className="volume-wrap">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true" style={{ opacity: 0.5 }}>
                <path d="M8 2L4 6H1v4h3l4 4V2z" />
                <path d="M11 5a4 4 0 0 1 0 6" stroke="currentColor" strokeWidth="1.2" fill="none" />
              </svg>
              <input
                type="range"
                className="volume-slider"
                min="0"
                max="1"
                step="0.05"
                value={volume}
                onChange={handleVolumeChange}
                aria-label="Volume"
              />
            </div>
          </div>
        </div>

        {/* Closing note */}
        <p className="player-note">
          The Kingdom does not close its gates.
        </p>
      </main>
    </div>
  );
}